import type {AutoNamingState} from 'common/hooks/auto-naming/types';
import type {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {EntityType} from 'common/report/util/saved-entities/types';

export interface CreateEntityModalState {
  description: string;
  existingEntityNames: Set<string>;
  saving: Promise<void>;
  name: string;
  submitAttempted: boolean;
  open: boolean;
  globalAccessType: string;
  isCompanyKey: boolean;
  companyKeyName: string;
  // Auto-naming state
  autoNaming: AutoNamingState | null;
  isEditingName: boolean;
}

export interface CreateEntityModalAttrs {
  closeable: boolean;
  'entity-type': EntityType;
  'sub-entity-type'?: string;
  'is-editing': boolean;
  'initial-description': string;
  'initial-name': string;
  'initial-company-key-name': string;
  'initial-company-revenue-key': string;
  'modal-title': string;
  open: boolean;
  warning: string;
  'project-id': number;
  'show-share-management': boolean;
  'force-share-flow': boolean;
  'show-global-access-menu': boolean;
  'visibility-controls-message': string;
  'show-copy-link-button': boolean;
  // Locks the B2B Company Key toggle off (and hides the company name input). Used when the company key is
  // fixed for the project, e.g. hierarchically sharded projects, so a new group can never become it.
  'disable-company-key': boolean;
}

export interface CreateEntityModalJsxProps {
  attrs: Partial<CreateEntityModalAttrs>;
  props?: {
    existingEntityNames?: string[];
    cohort?: Cohort; // Cohort instance for auto-naming
    autoNamingSource?: string; // Page source for tracking (e.g. 'users', 'insights')
  };
  on?: {
    save?: (
      ev: CustomEvent<{
        value: Pick<
          CreateEntityModalState,
          `name` | `description` | `globalAccessType` | `isCompanyKey` | `companyKeyName`
        >;
      }>,
    ) => void;
    cancel?: (ev: CustomEvent<void>) => void;
  };
}

export enum GlobalAccessType {
  OFF = `Just me`,
  VIEWER = `Everyone can view`,
  EDITOR = `Everyone can edit`,
}

export interface ModalValues {
  name: string;
  description: string;
  globalAccessType?: GlobalAccessType;
}

export interface ModalReturn extends ModalValues {
  clearOpenShareUponSave?: boolean;
  didSave: boolean;
  didShare: boolean;
  event: string;
  value?: ModalValues; // for saveAndShare case
}
